import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Container, Typography, Card, CardContent, Button, CircularProgress, Alert, Chip,
  TextField, Dialog, DialogTitle, DialogContent, DialogActions, Menu, MenuItem, Divider,
  Select, FormControl, InputLabel,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import RequireAuth from '../../components/athletes/RequireAuth';
import { useAuth } from '../../context/AuthContext';
import { getTeam, listTeamStaff } from '../../services/teamService';
import { hasProgramManagementAuthorityForTeam } from '../../services/programService';
import {
  listPlaybookFormations, createPlaybookFormation, updatePlaybookFormation, deletePlaybookFormation,
  listPlaybookPlays, deletePlaybookPlay,
} from '../../services/playbookService';
import {
  UNIT_TYPES, UNIT_TYPE_LABELS, structureTermLabel, structureTermPlural,
  emptyDocument, formatPlayersPerSide, resolvePlayersPerSide, playersPerSideToChoice,
} from '../../utils/playbook';
import { startersForUnitType, QUICK_POSITIONS, nextSpawnPosition } from '../../utils/playbookTemplates';
import { newMarkerId, resolveMarkerCollisions } from '../../utils/playbookMarkers';
import { FOOTBALL_TYPE_LABELS } from '../../utils/teamClassification';
import { FormatPicker, FootballTypePicker } from '../../components/teams/ClassificationPickers';
import TeamHubLayout from '../../components/teams/TeamHubLayout';
import FootballField from '../../components/playbook/FootballField';
import PlaybookMarker from '../../components/playbook/PlaybookMarker';
import {
  VIEW_WIDTH, VIEW_HEIGHT, fieldToScreen, screenToField, onLineBoundaryY, centerBoundaryY,
} from '../../components/playbook/fieldCoordinates';
import { PlayEditorView, PlayCard } from './TeamPlaybookPlayEditor';

// Playbook Phase A (034) — the reusable Formation/Front/Unit library.
// Phase B.0 (035) adds Plays — see TeamPlaybookPlayEditor.js — built
// FROM a saved Formation/Front/Unit plus a snapshotted opponent look.
//
// Phase B.2.2 — Formation-first library. The old "Formations tab" vs.
// "Plays tab" split (a flat, unrelated-feeling pair of libraries) is
// replaced by ONE grouped view: every Formation/Front/Unit renders as
// its own group, with the Plays built from it (via their persisted
// source_formation_id — never name-matching, never coordinate-matching)
// listed directly underneath, plus a prominent "+ Create Play" per
// group that skips the old redundant "choose a starting Formation"
// step entirely (see PlayEditorView's own `presetFormation` prop in
// TeamPlaybookPlayEditor.js). Plays with no matching source Formation
// (pre-existing data, or a source Formation deleted since — migration
// 035's source_formation_id is ON DELETE SET NULL, never CASCADE) still
// render, in their own "Standalone Plays" section — never hidden.
//
// Still no routes/blocks/motion changes this pass, no Vision/Threats, no
// Pre-Snap, no Study View, no Film Study, no publishing — see 035's own
// header comments and this phase's own final report for the full
// architecture reasoning and explicit "not built" list.

/** "11 / 11 placed", "10 / 11 placed — 1 player needed", "12 / 11 placed
 * — 1 extra player" — plain when the count matches (or the format isn't
 * chosen yet), a ⚠ prefix + correction hint otherwise. Singular/plural
 * handled explicitly rather than always saying "players". */
function placedCountLabel(placed, expected) {
  if (expected === null || expected === undefined) return { text: `${placed} / — placed`, warn: false };
  if (placed === expected) return { text: `${placed} / ${expected} placed`, warn: false };
  if (placed < expected) {
    const short = expected - placed;
    return { text: `${placed} / ${expected} placed — ${short} player${short === 1 ? '' : 's'} needed`, warn: true };
  }
  const extra = placed - expected;
  return { text: `${placed} / ${expected} placed — ${extra} extra player${extra === 1 ? '' : 's'}`, warn: true };
}

/** The field + marker editing surface. Composes the pure FootballField/
 * PlaybookMarker renderers with drag/select/label-edit interaction —
 * this is the ONLY place in Phase A that mutates marker positions;
 * FootballField/PlaybookMarker themselves stay presentation-only so a
 * future read-only Film Study view can reuse them without any of this
 * editing logic. Pointer Events (not mouse-only handlers) so mouse,
 * touch, and pen share one interaction model. */
function MarkerField({ document: doc, onChangeMarkers, readOnly, unitType, expectedCount }) {
  const wrapperRef = useRef(null);
  const [selectedId, setSelectedId] = useState(null);
  const draggingId = useRef(null);
  const [addMenuAnchor, setAddMenuAnchor] = useState(null);
  const [customLabelMode, setCustomLabelMode] = useState(false);
  const [customLabelText, setCustomLabelText] = useState('');

  const clientToField = (clientX, clientY) => {
    const rect = wrapperRef.current.getBoundingClientRect();
    const px = ((clientX - rect.left) / rect.width) * VIEW_WIDTH;
    const py = ((clientY - rect.top) / rect.height) * VIEW_HEIGHT;
    return screenToField(doc.field_config, px, py);
  };

  const updateMarker = (id, patch) => {
    onChangeMarkers(doc.markers.map(m => (m.id === id ? { ...m, ...patch } : m)));
  };

  const handleMarkerPointerDown = (id) => (e) => {
    if (readOnly) return;
    e.stopPropagation();
    e.target.setPointerCapture(e.pointerId);
    draggingId.current = id;
    setSelectedId(id);
  };

  const handleMarkerPointerMove = (e) => {
    if (readOnly || !draggingId.current) return;
    const { x, y } = clientToField(e.clientX, e.clientY);
    const clampedX = Math.max(-1, Math.min(1, x));
    // Formation-building placement boundary: the active/current
    // formation being authored here stays on its own side. This is an
    // editor-interaction clamp only, not a stored/global coordinate
    // restriction — nothing in the schema forbids y > 0, so a future
    // Post-Snap/PTP phase remains free to move markers across the LOS.
    // The boundary itself is geometry-derived, not a flat y <= 0: a
    // marker's front (toward-LOS) VISIBLE EDGE, not merely its center
    // point, is what must stay on the current-unit side of the LOS
    // band — see fieldCoordinates.js's onLineBoundaryY()/centerBoundaryY()
    // for the derivation. The offensive Center ("C" role_label, exact
    // match only — no attempt to infer this for arbitrary custom roles)
    // gets the one deliberate forward exception, since he's the player
    // who controls/snaps the ball; every other marker (offense or
    // defense — there is no Center exception for defense) uses the
    // normal boundary.
    const draggedMarker = doc.markers.find(m => m.id === draggingId.current);
    const boundary = draggedMarker?.role_label === 'C'
      ? centerBoundaryY(doc.field_config)
      : onLineBoundaryY(doc.field_config);
    const clampedY = Math.min(boundary, y);
    // Formation Editor collision guard: after the boundary clamp above,
    // also stop the dragged marker's visible circle from overlapping
    // any other marker's — see resolveMarkerCollisions()'s own header
    // note for why this is drag-only and never persisted as a
    // data-model rule.
    const resolved = resolveMarkerCollisions(doc.field_config, draggingId.current, { x: clampedX, y: clampedY }, doc.markers);
    updateMarker(draggingId.current, { start: resolved });
  };

  const handleMarkerPointerUp = () => {
    draggingId.current = null;
  };

  const addPlayerAt = (label, position) => {
    if (readOnly) return;
    const id = newMarkerId();
    const candidate = position || nextSpawnPosition(doc.markers.length);
    // Run every spawn candidate through the same collision guard used
    // for dragging — nextSpawnPosition()'s own grid spacing is already
    // wide enough to avoid overlap on its own for players spawned one
    // at a time, but this also protects against the case where a
    // starter template is already loaded (its markers don't follow the
    // spawn grid at all) or a non-default field_config changes the
    // yards-per-viewBox-unit ratio. `id` doesn't exist in doc.markers
    // yet, so nothing is excluded from the comparison.
    const spawn = resolveMarkerCollisions(doc.field_config, id, candidate, doc.markers);
    const newMarker = { id, role_label: label || 'Player', start: spawn };
    onChangeMarkers([...doc.markers, newMarker]);
    setSelectedId(id);
  };

  const quickPositions = QUICK_POSITIONS[unitType] || [];

  const handleAddPlayerClick = (e) => {
    if (readOnly) return;
    if (quickPositions.length === 0) {
      // No quick-pick vocabulary defined for this book (Special Teams,
      // this pass) — keep prior behavior, just with the fixed spawn
      // position instead of stacking everything at (0,0).
      addPlayerAt('Player');
      return;
    }
    setAddMenuAnchor(e.currentTarget);
  };

  const closeAddMenu = () => {
    setAddMenuAnchor(null);
    setCustomLabelMode(false);
    setCustomLabelText('');
  };

  const handleQuickPositionPick = (label) => {
    addPlayerAt(label);
    closeAddMenu();
  };

  const handleCustomLabelConfirm = () => {
    addPlayerAt(customLabelText.trim() || 'Player');
    closeAddMenu();
  };

  const handleRemoveMarker = (id) => {
    onChangeMarkers(doc.markers.filter(m => m.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  const selectedMarker = doc.markers.find(m => m.id === selectedId) || null;
  const countLabel = placedCountLabel(doc.markers.length, expectedCount ?? null);

  return (
    <Box>
      <Box ref={wrapperRef} sx={{ maxWidth: { xs: '100%', md: 900 }, mx: 'auto' }}>
        <FootballField fieldConfig={doc.field_config} onFieldPointerDown={() => setSelectedId(null)}>
          {doc.markers.map(m => {
            const { px, py } = fieldToScreen(doc.field_config, m.start.x, m.start.y);
            return (
              <PlaybookMarker
                key={m.id} px={px} py={py} label={m.role_label} selected={m.id === selectedId}
                onPointerDown={readOnly ? undefined : handleMarkerPointerDown(m.id)}
                onPointerMove={readOnly ? undefined : handleMarkerPointerMove}
                onPointerUp={readOnly ? undefined : handleMarkerPointerUp}
              />
            );
          })}
        </FootballField>
      </Box>

      {!readOnly && (
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
            <Button variant="outlined" color="primary" onClick={handleAddPlayerClick} sx={{ textTransform: 'none', fontWeight: 700 }}>
              + Add Player
            </Button>
            <Typography sx={{ fontWeight: 700, color: countLabel.warn ? '#f5a623' : '#9eb8d0' }}>
              {countLabel.warn ? '⚠ ' : ''}{countLabel.text}
            </Typography>
            <Menu anchorEl={addMenuAnchor} open={Boolean(addMenuAnchor)} onClose={closeAddMenu}>
              {!customLabelMode ? (
                [
                  ...quickPositions.map(pos => (
                    <MenuItem key={pos} onClick={() => handleQuickPositionPick(pos)} sx={{ fontWeight: 700, minWidth: 140 }}>
                      {pos}
                    </MenuItem>
                  )),
                  <Divider key="__divider" />,
                  <MenuItem key="__custom" onClick={() => setCustomLabelMode(true)}>
                    Custom…
                  </MenuItem>,
                ]
              ) : (
                <Box sx={{ px: 1.5, py: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    size="small" autoFocus value={customLabelText} placeholder="H, Y, Rover…"
                    onChange={e => setCustomLabelText(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleCustomLabelConfirm(); }}
                    sx={{ width: 140 }}
                  />
                  <Button size="small" variant="contained" onClick={handleCustomLabelConfirm} sx={{ textTransform: 'none', fontWeight: 700 }}>
                    Add
                  </Button>
                </Box>
              )}
            </Menu>
          </Box>

          {selectedMarker && (
            <Card sx={{ backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a' }}>
              <CardContent sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                  label="Role label" size="small" value={selectedMarker.role_label}
                  onChange={e => updateMarker(selectedMarker.id, { role_label: e.target.value })}
                  placeholder="QB, X, Mike, CB1…"
                  sx={{ width: { xs: '100%', sm: 320 }, maxWidth: 320 }}
                />
                <Button size="small" color="error" onClick={() => handleRemoveMarker(selectedMarker.id)} sx={{ textTransform: 'none', fontWeight: 700 }}>
                  Remove
                </Button>
              </CardContent>
            </Card>
          )}
        </Box>
      )}
    </Box>
  );
}

/** Create/Edit Formation — one view, reused for both. formation=null
 * means create. Format fields reuse the same shared pickers/resolvers
 * already established for Team-level classification/format (028/029) —
 * no new picker UI invented for Playbook. */
function FormationEditorView({ teamId, unitType, team, formation, onSaved, onCancel }) {
  const term = structureTermLabel(unitType);
  const [name, setName] = useState(formation?.name || '');
  const initialFormat = playersPerSideToChoice(formation?.players_per_side ?? team.players_per_side ?? null);
  const [formatChoice, setFormatChoice] = useState(initialFormat.choice);
  const [formatOtherText, setFormatOtherText] = useState(initialFormat.otherText);
  const [footballType, setFootballType] = useState(formation?.football_type || team.football_type || '');
  const [doc, setDoc] = useState(() => formation?.document || emptyDocument(unitType));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [starterKey, setStarterKey] = useState('');

  const resolvedPlayersPerSide = resolvePlayersPerSide(formatChoice, formatOtherText);

  const handleChangeMarkers = (markers) => setDoc(d => ({ ...d, markers }));

  // Starter templates — 11v11 only this pass (product decision), and
  // only offered when creating a brand-new formation, never on an
  // existing one (avoids silently clobbering a coach's saved work).
  // Applying one just replaces doc.markers with the template's starting
  // positions/labels — every marker remains fully editable afterward,
  // exactly like one placed by hand. Picking it is a one-time starting
  // point, not a persisted link: nothing records which (if any) template
  // a saved Formation started from, so later edits never "snap back."
  const starters = startersForUnitType(unitType);
  const showStarterPicker = !formation && resolvedPlayersPerSide === 11 && starters.length > 0;

  const handleStarterChange = (key) => {
    setStarterKey(key);
    if (!key) { setDoc(d => ({ ...d, markers: [] })); return; }
    const template = starters.find(t => t.key === key);
    if (!template) return;
    const markers = template.markers.map(m => ({
      id: newMarkerId(), role_label: m.role_label, start: { x: m.x, y: m.y },
    }));
    setDoc(d => ({ ...d, markers }));
  };

  const handleSave = async () => {
    setError(null);
    if (!name.trim()) { setError('Name is required.'); return; }
    if (resolvedPlayersPerSide === null) { setError('Choose the players-per-side format.'); return; }
    if (resolvedPlayersPerSide < 4 || resolvedPlayersPerSide > 15) { setError('Players per side must be between 4 and 15.'); return; }

    setSaving(true);
    try {
      if (formation) {
        await updatePlaybookFormation({
          formationId: formation.id, name: name.trim(), playersPerSide: resolvedPlayersPerSide,
          footballType: footballType || null, document: doc,
        });
      } else {
        await createPlaybookFormation({
          teamId, unitType, name: name.trim(), playersPerSide: resolvedPlayersPerSide,
          footballType: footballType || null, document: doc,
        });
      }
      await onSaved();
    } catch (e) {
      setError(e.message || `Could not save this ${term.toLowerCase()}.`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 0.5 }}>
        <Typography sx={{ fontWeight: 800 }}>{formation ? `Edit ${term}` : `New ${term}`}</Typography>
        <Button
          variant="outlined" size="small" onClick={() => setSettingsOpen(true)}
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          ⚙ {term} Settings
        </Button>
      </Box>

      {/* Makes the resolved format visible without opening Settings — on
          create, this is already defaulted from the current Team's own
          players_per_side/football_type (see the initialFormat/footballType
          useState() calls above); this line just surfaces that default so
          it reads as clearly team-derived rather than silently blank. */}
      <Typography sx={{ color: '#6a8aa8', fontSize: '0.8rem', mb: 2 }}>
        {resolvedPlayersPerSide ? formatPlayersPerSide(resolvedPlayersPerSide) : 'No format selected yet'}
        {footballType && ` · ${FOOTBALL_TYPE_LABELS[footballType] || footballType}`}
      </Typography>

      <TextField
        fullWidth label={`${term} name`} value={name} onChange={e => setName(e.target.value)} required
        placeholder={unitType === 'offense' ? 'Trips Right' : unitType === 'defense' ? '4-3 Under' : 'Kickoff'}
        sx={{ mb: 2 }}
      />

      {showStarterPicker && (
        <FormControl size="small" sx={{ mb: 2, minWidth: 220 }}>
          <InputLabel id="starter-formation-label">Starter Formation</InputLabel>
          <Select
            labelId="starter-formation-label" label="Starter Formation" value={starterKey}
            onChange={e => handleStarterChange(e.target.value)}
          >
            <MenuItem value="">Blank / Custom</MenuItem>
            {starters.map(t => (
              <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <MarkerField
        document={doc} onChangeMarkers={handleChangeMarkers} readOnly={false}
        unitType={unitType} expectedCount={resolvedPlayersPerSide}
      />

      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 1.5, mt: 3 }}>
        <Button variant="contained" color="primary" onClick={handleSave} disabled={saving} sx={{ textTransform: 'none', fontWeight: 800, px: 4 }}>
          {saving ? <CircularProgress size={20} sx={{ color: '#050c16' }} /> : 'Save'}
        </Button>
        <Button onClick={onCancel} disabled={saving} sx={{ textTransform: 'none', color: '#6a8aa8' }}>
          Cancel
        </Button>
      </Box>

      <Dialog
        open={settingsOpen} onClose={() => setSettingsOpen(false)}
        PaperProps={{ sx: { backgroundColor: 'rgba(10,20,34,0.98)', border: '1px solid #1e2a3a' } }}
      >
        <DialogTitle sx={{ fontWeight: 900 }}>{term} Settings</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, minWidth: 280 }}>
          <Box>
            <Typography sx={{ color: '#6a8aa8', fontSize: '0.75rem', mb: 0.5 }}>Players per side</Typography>
            <FormatPicker choice={formatChoice} otherText={formatOtherText} onChoiceChange={setFormatChoice} onOtherTextChange={setFormatOtherText} />
          </Box>
          <Box>
            <Typography sx={{ color: '#6a8aa8', fontSize: '0.75rem', mb: 0.5 }}>Football type (optional)</Typography>
            <FootballTypePicker value={footballType} onChange={setFootballType} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="contained" color="primary" onClick={() => setSettingsOpen(false)} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/** Phase B.2.2 — one Formation/Front/Unit GROUP: its own header (name,
 * format, placed-count, Edit/Delete — the same content FormationCard
 * used to show standalone) plus the Plays built from it, listed
 * directly underneath. `formationPlays` is pre-filtered by the caller
 * via source_formation_id (never derived here by name/coordinate
 * matching — see this file's own header note). "+ Create Play" is
 * always a full-size, always-visible button — never buried in an
 * overflow menu (§9 of the B.2.2 spec). */
function FormationGroup({ formation, formationPlays, unitType, canWrite, onEditFormation, onDeleteFormation, onCreatePlay, onEditPlay, onDeletePlay }) {
  const placedCount = (formation.document?.markers || []).length;
  const mismatch = placedCount !== formation.players_per_side;
  const term = structureTermLabel(unitType);
  return (
    <Card sx={{ backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a' }}>
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.05rem' }}>{formation.name}</Typography>
            <Typography sx={{ color: '#9eb8d0', fontSize: '0.85rem', mt: 0.25 }}>
              {formatPlayersPerSide(formation.players_per_side)}
              {formation.football_type && ` · ${FOOTBALL_TYPE_LABELS[formation.football_type] || formation.football_type}`}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
              <Typography sx={{ color: '#6a8aa8', fontSize: '0.8rem' }}>
                {placedCount} / {formation.players_per_side} placed
              </Typography>
              {mismatch && (
                <Chip size="small" label="Mismatch" sx={{ fontWeight: 700, bgcolor: '#2a1a05', color: '#f5a623' }} />
              )}
            </Box>
          </Box>
          {canWrite && (
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              <Button size="small" onClick={() => onEditFormation(formation)} sx={{ textTransform: 'none', fontWeight: 700, minHeight: 40 }}>
                Edit {term}
              </Button>
              <Button size="small" color="error" onClick={() => onDeleteFormation(formation)} sx={{ textTransform: 'none', fontWeight: 700, minHeight: 40 }}>
                Delete
              </Button>
              <Button
                size="small" variant="contained" color="primary" onClick={() => onCreatePlay(formation)}
                sx={{ textTransform: 'none', fontWeight: 700, minHeight: 40 }}
              >
                + Create Play
              </Button>
            </Box>
          )}
        </Box>

        {formationPlays.length === 0 ? (
          <Typography sx={{ color: '#6a8aa8', fontSize: '0.85rem', fontStyle: 'italic' }}>
            No Plays yet from this {term.toLowerCase()}.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pl: { xs: 0, sm: 2 }, borderLeft: { sm: '2px solid #1e2a3a' } }}>
            {formationPlays.map(p => (
              <PlayCard key={p.id} play={p} canWrite={canWrite} onEdit={onEditPlay} onDeleteClick={onDeletePlay} />
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

function TeamPlaybookInner() {
  const { teamId } = useParams();
  const { currentUser } = useAuth();
  const [team, setTeam] = useState(null);
  const [myRoles, setMyRoles] = useState([]);
  const [hasProgramAuthority, setHasProgramAuthority] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [unitType, setUnitType] = useState('offense');
  // Phase B.2.2 — Formations and Plays now load TOGETHER for one book
  // (unitType), since the primary view groups Plays under their source
  // Formation (via source_formation_id — see loadLibrary() below). This
  // replaces the old separate "structures library" / "Plays library"
  // (PlaybookPlayLibrary, now removed) with one combined list/loading/
  // error state.
  const [formations, setFormations] = useState([]);
  const [plays, setPlays] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);

  const [editingFormation, setEditingFormation] = useState(undefined); // undefined = library view, null = create, object = edit
  // null = library view; { mode: 'create', formation } = "+ Create Play"
  // from that Formation (formation may be undefined for a Play with no
  // preset source — see the "no generic + New Play" decision in this
  // phase's own report); { mode: 'edit', play } = editing an existing Play.
  const [playEditorState, setPlayEditorState] = useState(null);

  // Formation delete and Play delete keep SEPARATE confirm state
  // (different messages, different service calls) rather than a single
  // over-generalized "delete anything" abstraction — matches this file's
  // own established per-concern separation style.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [deletePlayTarget, setDeletePlayTarget] = useState(null);
  const [deletingPlay, setDeletingPlay] = useState(false);
  const [deletePlayError, setDeletePlayError] = useState(null);

  useEffect(() => {
    Promise.all([getTeam(teamId), listTeamStaff(teamId), hasProgramManagementAuthorityForTeam(teamId)])
      .then(([t, staff, hasAuthority]) => {
        setTeam(t);
        setMyRoles(staff.filter(s => s.user_id === currentUser?.id && s.status === 'active').map(s => s.role));
        setHasProgramAuthority(hasAuthority);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  // Assistant Coach is deliberately included here — Playbook write
  // authority is intentionally broader than Schedule/Board's in this
  // phase (034's own documented product decision), since Playbook is
  // collaborative coaching content. Read and write authority are
  // identical in Phase A: there is no separate "read only staff" tier
  // beyond the connected-guardian carve-out below, and platform admin is
  // never specially detected client-side here — same established
  // convention as every other Team Hub page (it remains a real,
  // independent backend bypass inside every RPC regardless).
  const hasPlaybookAccess = Boolean(
    hasProgramAuthority || myRoles.some(r => ['team_admin', 'head_coach', 'assistant_coach'].includes(r))
  );

  const loadLibrary = React.useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      const [f, p] = await Promise.all([
        listPlaybookFormations(teamId, unitType),
        listPlaybookPlays(teamId, unitType),
      ]);
      setFormations(f);
      setPlays(p);
    } catch (e) {
      setListError(e.message);
    } finally {
      setListLoading(false);
    }
  }, [teamId, unitType]);

  useEffect(() => {
    if (hasPlaybookAccess) loadLibrary();
    else setListLoading(false);
  }, [hasPlaybookAccess, loadLibrary]);

  const handleUnitTypeChange = (nextUnitType) => {
    setUnitType(nextUnitType);
    setEditingFormation(undefined);
    setPlayEditorState(null);
  };

  const handleFormationSaved = async () => {
    setEditingFormation(undefined);
    await loadLibrary();
  };

  const handlePlaySaved = async () => {
    setPlayEditorState(null);
    await loadLibrary();
  };

  // Plays built from a Formation that gets deleted survive it (migration
  // 035: source_formation_id is ON DELETE SET NULL, never CASCADE) — a
  // coach deleting a Formation with existing Plays should know those
  // Plays are about to move into "Standalone Plays", not disappear.
  const formationDeletePlayCount = deleteTarget
    ? plays.filter(p => p.source_formation_id === deleteTarget.id).length
    : 0;

  const handleDeleteConfirm = async () => {
    setDeleteError(null);
    setDeleting(true);
    try {
      await deletePlaybookFormation(deleteTarget.id);
      setDeleteTarget(null);
      await loadLibrary();
    } catch (e) {
      setDeleteError(e.message || 'Could not delete this item.');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeletePlayConfirm = async () => {
    setDeletePlayError(null);
    setDeletingPlay(true);
    try {
      await deletePlaybookPlay(deletePlayTarget.id);
      setDeletePlayTarget(null);
      await loadLibrary();
    } catch (e) {
      setDeletePlayError(e.message || 'Could not delete this Play.');
    } finally {
      setDeletingPlay(false);
    }
  };

  if (loading) return <Box sx={{ py: 10, textAlign: 'center' }}><CircularProgress sx={{ color: '#2ea84b' }} /></Box>;
  if (error) return <Container maxWidth="sm" sx={{ py: 6 }}><Alert severity="error">{error}</Alert></Container>;
  if (!team) return null;

  if (!hasPlaybookAccess) {
    return (
      <TeamHubLayout team={team} active="playbook">
        <Card sx={{ backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a' }}>
          <CardContent sx={{ py: 7, textAlign: 'center' }}>
            <Typography sx={{ fontSize: '2.2rem', mb: 1.5 }}>📋</Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Playbook</Typography>
            <Typography sx={{ color: '#9eb8d0', maxWidth: 460, mx: 'auto' }}>
              This Team's Playbook isn't available to your account yet.
            </Typography>
          </CardContent>
        </Card>
      </TeamHubLayout>
    );
  }

  // Grouping is ALWAYS by the persisted source_formation_id — never by
  // matching names, never by comparing marker coordinates (§10 of the
  // B.2.2 spec). A Play whose source_formation_id doesn't match any
  // CURRENTLY-LOADED Formation (null, because it was never linked, or
  // because its source Formation was since deleted) falls through to
  // orphanPlays — never hidden, never deleted, never heuristically
  // relinked.
  const orphanPlays = plays.filter(p => !formations.some(f => f.id === p.source_formation_id));
  const term = structureTermLabel(unitType);

  // Phase B.2.4 — field-first UX. TeamHubLayout's own maxWidth prop (an
  // existing extensibility point — TeamHubLayout.js itself is NOT
  // modified) is widened only while the Play/Formation editor workspace
  // is actually open, so the football field can use most of the
  // available viewport width instead of being capped at the page-wide
  // 'md' (900px) default that made every editor feel like a narrow
  // webpage form. The Playbook LIBRARY view (Formation groups/
  // Standalone Plays) keeps the normal 'md' width — this is deliberately
  // scoped to the editor workspace only, never a redesign of Team
  // navigation/layout generally.
  // Scoped to the PLAY editor specifically (not FormationEditorView) —
  // this task's field-first pass concerns the Play Editor workspace
  // only; FormationEditorView's own MarkerField already carries its own
  // separate 900px internal cap, untouched here.
  const editorOpen = Boolean(playEditorState);

  return (
    <TeamHubLayout team={team} active="playbook" maxWidth={editorOpen ? false : 'md'}>
      {editingFormation !== undefined ? (
        <FormationEditorView
          teamId={teamId} unitType={unitType} team={team}
          formation={editingFormation}
          onSaved={handleFormationSaved}
          onCancel={() => setEditingFormation(undefined)}
        />
      ) : playEditorState ? (
        <PlayEditorView
          teamId={teamId} unitType={unitType} team={team}
          play={playEditorState.mode === 'edit' ? playEditorState.play : null}
          presetFormation={playEditorState.mode === 'create' ? playEditorState.formation : null}
          onSaved={handlePlaySaved}
          onCancel={() => setPlayEditorState(null)}
        />
      ) : (
        <>
          <Box sx={{ display: 'flex', gap: 0.75, mb: 2, flexWrap: 'wrap' }}>
            {UNIT_TYPES.map(ut => (
              <Button
                key={ut} onClick={() => handleUnitTypeChange(ut)}
                variant={ut === unitType ? 'contained' : 'outlined'} color="primary" size="small"
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                {UNIT_TYPE_LABELS[ut]}
              </Button>
            ))}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Typography sx={{ fontWeight: 800 }}>{structureTermPlural(unitType)}</Typography>
            <Button
              variant="contained" color="primary" size="small" onClick={() => setEditingFormation(null)}
              sx={{ textTransform: 'none', fontWeight: 700, minHeight: 40 }}
            >
              + New {term}
            </Button>
          </Box>

          {listLoading ? (
            <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress sx={{ color: '#2ea84b' }} /></Box>
          ) : listError ? (
            <Alert severity="error">{listError}</Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {formations.length === 0 && orphanPlays.length === 0 && (
                <Card sx={{ backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a' }}>
                  <CardContent sx={{ textAlign: 'center', py: 5 }}>
                    <Typography sx={{ color: '#9eb8d0' }}>
                      No {structureTermPlural(unitType).toLowerCase()} yet. Create one to start building Plays.
                    </Typography>
                  </CardContent>
                </Card>
              )}

              {formations.map(f => (
                <FormationGroup
                  key={f.id} formation={f} unitType={unitType} canWrite={hasPlaybookAccess}
                  formationPlays={plays.filter(p => p.source_formation_id === f.id)}
                  onEditFormation={setEditingFormation}
                  onDeleteFormation={setDeleteTarget}
                  onCreatePlay={(formation) => setPlayEditorState({ mode: 'create', formation })}
                  onEditPlay={(play) => setPlayEditorState({ mode: 'edit', play })}
                  onDeletePlay={setDeletePlayTarget}
                />
              ))}

              {orphanPlays.length > 0 && (
                <Card sx={{ backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a' }}>
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box>
                      <Typography sx={{ fontWeight: 800 }}>Standalone Plays</Typography>
                      <Typography sx={{ color: '#6a8aa8', fontSize: '0.8rem', mt: 0.25 }}>
                        Not linked to a saved {term.toLowerCase()} — from before this Formation-first view, or its source {term.toLowerCase()} was deleted.
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {orphanPlays.map(p => (
                        <PlayCard
                          key={p.id} play={p} canWrite={hasPlaybookAccess}
                          onEdit={(play) => setPlayEditorState({ mode: 'edit', play })}
                          onDeleteClick={setDeletePlayTarget}
                        />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              )}
            </Box>
          )}
        </>
      )}

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => { if (!deleting) setDeleteTarget(null); }}
        PaperProps={{ sx: { backgroundColor: 'rgba(10,20,34,0.98)', border: '1px solid #1e2a3a' } }}
      >
        <DialogTitle sx={{ fontWeight: 900 }}>Delete {deleteTarget ? structureTermLabel(deleteTarget.unit_type) : ''}?</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1.5 }}>This permanently deletes <strong>{deleteTarget?.name}</strong>. This cannot be undone.</Typography>
          {formationDeletePlayCount > 0 && (
            <Typography sx={{ color: '#f5a623', fontSize: '0.85rem' }}>
              {formationDeletePlayCount} Play{formationDeletePlayCount === 1 ? '' : 's'} built from this {structureTermLabel(deleteTarget?.unit_type).toLowerCase()} will move to Standalone Plays — they will not be deleted.
            </Typography>
          )}
          {deleteError && <Alert severity="error" sx={{ mt: 1 }}>{deleteError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting} sx={{ textTransform: 'none', color: '#9eb8d0' }}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} disabled={deleting} variant="contained" color="error" sx={{ textTransform: 'none', fontWeight: 700 }}>
            {deleting ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deletePlayTarget)}
        onClose={() => { if (!deletingPlay) setDeletePlayTarget(null); }}
        PaperProps={{ sx: { backgroundColor: 'rgba(10,20,34,0.98)', border: '1px solid #1e2a3a' } }}
      >
        <DialogTitle sx={{ fontWeight: 900 }}>Delete Play?</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1.5 }}>This permanently deletes <strong>{deletePlayTarget?.name}</strong>. This cannot be undone. The source Formation/Front/Unit it was built from is not affected.</Typography>
          {deletePlayError && <Alert severity="error" sx={{ mt: 1 }}>{deletePlayError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDeletePlayTarget(null)} disabled={deletingPlay} sx={{ textTransform: 'none', color: '#9eb8d0' }}>Cancel</Button>
          <Button onClick={handleDeletePlayConfirm} disabled={deletingPlay} variant="contained" color="error" sx={{ textTransform: 'none', fontWeight: 700 }}>
            {deletingPlay ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </TeamHubLayout>
  );
}

export default function TeamPlaybookPage() {
  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Navbar />
      <RequireAuth><TeamPlaybookInner /></RequireAuth>
    </Box>
  );
}
